// import payPalService from "@/services/paypal";
import {mapActions, mapGetters} from "vuex";
import sendEmailUtil from "@/utils/send-email.util"
import Decimal from 'decimal.js'
import analyticsMixin from "../analytics.mixin";
import copyWorkflowFiles from '@/services/copy-workflow-files.service'
import getSecret from "@/services/get-secret.service"
import createPaymentIntent from "@/services/stripe/create-payment-intent.service"
import { loadStripe } from '@stripe/stripe-js';

export default {
  mixins: [analyticsMixin],

  components: {
    CostInfo: () => import('@/components/CostInfo.vue'),
    Carousel: () => import('@/components/PayPal/Carousel.vue'),
    FindCoupon: () => import('@/components/FindCoupon.vue'),
  },

  props: {
    level: {
      type: Number,
      default: 1,
    },

    type: {
      type: String,
      default: 'collaborative'
    },

    userId: {
      type: String,
      require: true,
    },

    formId: {
      type: String,
      default: ''
    },
  },

  data() {
    return {
      // paypalClientId: '',
      tabStatus: "active",
      // paypalBtnsLoaded: false,
      dialog: false,
      isChild: true,
      payInfo: {},
      submitted: false,
      message: "",
      isGoodToken: true,
      loading: false,
      // paypal: {},
      cost: 0,
      originalCost: 0,
      couponKey: '',
      workflowType: null,
      processingPayment: false,
      stripe: {
        publicKey: null,
        instance: null,
        cardElement: null,
        cardComplete: false
      }
    };
  },

  computed: {
    ...mapGetters(["baseUrl", "loadingStatus"]),

    ...mapGetters('analytics', ['awID', 'paywallIds']),

    ...mapGetters("coupons", ["currentCoupon", "coupons"]),

    ...mapGetters("prices", ["getPriceByLevel", "getCardCosts"]),

    ...mapGetters("profile", ["alreadyUsedToken", "userInfo", "getPayments"]),

    ...mapGetters("workflowTypes", ["typeByTitle"]),

    costBody() {
      if(this.workflowType) {
        const typeInfo = this.workflowType.costs[this.payInfo.type]
        const keyValuePairs = [
          { key: 'p1', value: typeInfo.paywall1 },
          { key: 'p2', value: typeInfo.paywall2 }
        ]
        return this.bodyKeyValReplacement(this.costInfoData.items, keyValuePairs)
      } else {
        const key = this.payInfo.level == 1 ? 'p1' : 'p2'
        return this.bodyKeyValReplacement(this.costInfoData.items, [{ key, value: this.costPrice }])
      }
    },

    costPrice() {
      if(this.workflowType) {
        const typeInfo = this.workflowType.costs[this.payInfo.type]
        return parseInt(typeInfo[`paywall${this.payInfo.level}`])
      }
      return this.costInfoData.price
    },

    costTypeIndex() {
      return this.$route.params.type === 'collaborative' ? 0 : 1
    },

    couponSaved() {
      return ![null, 'null', undefined].includes(localStorage.getItem(this.couponKey))
    },

    costInfoData() {
      const data = this.getPriceByLevel(this.payInfo.type,`level_${this.payInfo.level}`);
      return data;
    },

    pmt() {
      return this.getPriceByLevel(this.payInfo.type,`level_${this.$route.params.level}`)
    },

    showPayWall() {
      return this.loading === false && this.costInfoData && this.payInfo.formId.length > 0 && !this.$route?.query?.token
    }
  },

  created() {
    if (this.$route?.params) {
      this.isChild = false
    }

    this.payInfo = {
      type: this.$route?.params?.type || this.type,
      level: this.$route?.params?.level || this.level,
      userId: this?.$route?.params?.userId || this.userId,
      formId: this?.$route?.params?.formId || this.formId
    }

    if(this.workflowType === null) {
      const formName = this.payInfo.level === '1' ? 'workflows-published' : 'workflows-user'
      this.getRawForm({formName, formId: this.payInfo.formId}).then((wf) => {
        const wfTypeTitle = wf.type
        this.workflowType = this.typeByTitle(wfTypeTitle)
      })
    }
  },

  async mounted() {
    this.loading = true
    const stripeResp = await getSecret('StripePublicKey');
    this.stripe.publicKey = stripeResp.pk;
    this.stripe.instance = await loadStripe(this.stripe.publicKey);

    if(this.stripe.instance === null) {
      this.$logger.error('Stripe failed to initialize.')
      this.setAlert({ type: 'error', message: 'Payment system failed to load. Please refresh the page and try again.' })
    }

    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    await this.establishUserAuth()
    await this.setPrices();
    // this.paypal = payPalService();
    // this.paypalClientId = await this.paypal.getClientId()
    this.couponKey = `${this.userInfo.uid}-coupon_code`
    if (this.$route?.params?.level != 2 && !this.$route?.query?.token) {
      localStorage.removeItem(this.couponKey)
    }

    await this.hasPaidForLevel()

    if (this.userInfo.is_petitioner === false) {
      void this.$router.push(`/respondent-guide/${this.$route.params.formId}`)
      return
    }

    this.calculateCost()
    this.originalCost = this.costPrice
    if (localStorage.getItem(this.couponKey)) {
      if (this.$route?.query?.token) {
        const coupon_code = localStorage.getItem(this.couponKey)
        await this.findCoupon(coupon_code)
        this.deductAmount(false)
      }
    }

    // await this.handleToken();
    this.loading = false
  },

  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    if (this.stripe.cardElement) {
      this.stripe.cardElement.destroy();
    }
  },

  methods: {
    ...mapActions(["toggleLoadingOverlayStatus", "setOverlayMessage"]),

    ...mapActions("profile", ["setPayments", "establishUserAuth"]),

    ...mapActions("prices", ["setPrices"]),

    ...mapActions("workflowPublished", ["loadForm", "saveForm", "saveStep", "getRawForm", "updateFormDocs", 'clearPublishedWorkflows']),

    ...mapActions('coupons', ['findCoupon', 'getCoupons', 'resetCurrentCoupon', 'setCoupons']),

    ...mapActions("confirmation", ["confirm"]),

    bodyKeyValReplacement(body, keyValuePairs) {
      let replacedBody = body
      keyValuePairs.forEach(pair => {
        replacedBody = replacedBody.replace(`{{${pair.key}}}`, pair.value)
        replacedBody = replacedBody.replace(`{{${pair.key.toUpperCase()}}}`, pair.value)
      })
      return replacedBody
    },

    goBackToQuestionnaire() {
      this.confirm({
        title: "Are you sure?",
        accepted: async () => {
          this.loading = true
          await this.saveStep({ docId: this.payInfo.formId, step: 1 })
          this.loading = false
          void this.$router.push(`/legal-form-guide/divorce-forms/${this.payInfo.formId}`)
        },
      });
    },

    handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        this.tabStatus = "active";
        // keep this for now. See if this was needed from a previous story CU-868715za5
        //location.reload()
      } else {
        this.tabStatus = "inactive";
      }
    },
    cancelPayment() {
      this.dialog = false
      // this.paypalBtnsLoaded = false
      const childComponent = this.$refs.childComponentRef;
      if (childComponent && typeof childComponent.cancelPayment === 'function') {
        childComponent.cancelPayment();
      }
      this.cost = new Decimal(this.costPrice).toFixed(0)
    },
    async hasPaidForLevel() {
      await this.setPayments(this.payInfo.userId)
      const parentId = await this.getParentId()
      const hasPaid = this.getPayments[parentId]?.['level_' + this.$route.params.level]
      if(!hasPaid) return
      if(this.$route.params.level == 1) {
        void this.$router.push('/')
      } else {
        void this.$router.push(`/legal-form-guide/divorce-forms/${this.$route.params.formId}`)
      }
      return
    },
    calculateCost() {
      this.cost = new Decimal(this.costPrice || 0).toFixed(0)
    },
    showDialog() {
      this.resetCurrentCoupon()
      this.dialog = true

      const getPayment = Object.keys(this.getPayments)[0]
      const nestedObject = this.getPayments[getPayment];
      const level1Value = nestedObject?.amount_1;
      const level2Value = nestedObject?.amount_2;
      if((typeof level1Value !== 'undefined' && level1Value !== '' && this.$route?.params?.level === '1') || (typeof level2Value !== 'undefined' && level2Value !== '' && this.$route?.params?.level === '2')) {
        void this.$router.push('/')
      }

      this.$nextTick(() => {
        this.initializeStripe()
      })
    },

    initializeStripe() {
      try {
        const stripeBtnContainer = document.querySelector('#stripe-button-container')
        
        if (!stripeBtnContainer) {
          console.warn('Stripe container not found')
          return
        }
        
        if (!this.stripe.instance) {
          console.error('Stripe instance not initialized')
          this.setAlert({ type: 'error', message: 'Payment system not ready. Please refresh and try again.' })
          return
        }

        // Style the container with borders and padding
        stripeBtnContainer.style.border = '1px solid #ccc'
        stripeBtnContainer.style.borderRadius = '4px'
        stripeBtnContainer.style.padding = '12px'
        stripeBtnContainer.style.backgroundColor = '#ffffff'
        stripeBtnContainer.style.minHeight = '40px'
        stripeBtnContainer.style.fontFamily = 'Roboto, system-ui, sans-serif'
        
        const elements = this.stripe.instance.elements({
          locale: 'auto'
        })
        this.stripe.cardElement = elements.create('card', {
          style: {
            base: {
              fontSize: '16px',
              color: '#424242',
              fontFamily: 'Roboto, system-ui, sans-serif',
              fontSmoothing: 'antialiased',
              '::placeholder': {
                color: '#9ca3af'
              }
            },
            complete: {
              color: '#22c55e'
            },
            empty: {
              color: '#d1d5db'
            },
            focus: {
              color: '#1f2937'
            },
            invalid: {
              color: '#ef4444'
            }
          },
          hidePostalCode: false
        });
        this.stripe.cardElement.mount('#stripe-button-container');
        console.log('Stripe card element mounted successfully')
        
        this.stripe.cardElement.addEventListener('change', (event) => {
          if (event.error) {
            this.setAlert({ type: 'error', message: event.error.message })
            this.stripe.cardComplete = false;
          } else {
            this.stripe.cardComplete = event.complete;
          }
        });
      } catch (error) {
        console.error('Error initializing Stripe:', error)
        this.setAlert({ type: 'error', message: 'Failed to initialize payment form. Please refresh and try again.' })
      }

      // if(this.costPrice > 0) {
      //   const script = document.createElement('script');
      //   script.src = `https://www.paypal.com/sdk/js?client-id=${this.paypalClientId}`;
      //   script.async = true;
      //   script.defer = true;
      //   script.addEventListener('load', this.initializePayPalButton);
      //   document.head.appendChild(script);
      // } else {
      //   this.paypalBtnsLoaded = true
      // }
    },
    // initializePayPalButton() {
    //   const paypal = window.paypal;
    //
    //   const FUNDING_SOURCES = [
    //     paypal.FUNDING.PAYPAL,
    //     paypal.FUNDING.CARD
    //   ];
    //
    //   const buttonStyle = {
    //     layout: 'horizontal', // Set the layout to horizontal
    //     shape: 'rect',
    //     color: 'white' // You can change the color if needed
    //   };
    //
    //   FUNDING_SOURCES.forEach(e => {
    //     paypal.Buttons({
    //       fundingSource: e,
    //       style: buttonStyle,
    //       onInit: () => {
    //         this.paypalBtnsLoaded = true
    //       },
    //
    //       createOrder: (data, actions) => {
    //         return actions.order.create({
    //           purchase_units: [
    //             {
    //               amount: {
    //                 value: this.cost,
    //                 "currency_code": "USD",
    //               },
    //             },
    //           ],
    //         });
    //       },
    //
    //       onApprove: async (data, actions) => {
    //         // Show loading immediately (before any async operations)
    //         this.toggleLoadingOverlayStatus(true);
    //         this.setOverlayMessage('Processing payment...');
    //         
    //         const order = await actions.order.capture();
    //         if (order.status === "COMPLETED") {
    //           // Remove the Promise.all that includes sendPayment()
    //           // since sendPayment() doesn't seem to do much when cost > 0
    //           localStorage.setItem("paypalAmt", this.cost);
    //           await this.handlePayment(data.orderID);
    //         }
    //       },
    //     }).render('#paypal-button-container');
    //   })
    // },

    ...mapActions('alert', ['setAlert']),

    deductAmount(store = true) {
      const percentage = this.costPrice * (this.currentCoupon.amount / 100)
      this.cost = new Decimal(this.costPrice - percentage).toFixed(0)
      if (store && this.currentCoupon?.code && this.couponKey) {
        localStorage.setItem(this.couponKey, this.currentCoupon.code)
      }
    },

    // async handleToken() {
    //   this.toggleLoadingOverlayStatus(true);
    //   if (this.$route.query.token) {
    //     const {token} = this.$route.query
    //     if (!this.alreadyUsedToken(token)) {
    //       const response = await this.paypal.validToken(token);
    //       this.isGoodToken = response.data;
    //     } else {
    //       this.isGoodToken = false;
    //       this.setAlert({ type: 'error', message: 'Sorry, incorrect Paypal token.' })
    //     }
    //
    //     if (this.isGoodToken) {
    //       this.setOverlayMessage('Please wait while we build your questionnaire.')
    //       const amt = localStorage.getItem("paypalAmt")
    //       const {PayerID, paymentId} = this.$route.query;
    //
    //       const transaction = await this.paypal.success(PayerID, paymentId, amt)
    //       let transactionId = ''
    //       if (transaction?.data?.payment?.transactions?.[0]?.related_resources?.[0]?.sale?.id)
    //         transactionId = transaction.data.payment.transactions[0].related_resources[0].sale.id
    //
    //       await this.handlePayment(transactionId)
    //       this.setOverlayMessage()
    //     }
    //   } else {
    //     this.toggleLoadingOverlayStatus(false);
    //   }
    //   return
    // }

    async getParentId() {
      if (this.payInfo.level === "2") {
        return (await this.getRawForm({formId: this.$route?.params.formId})).parentId
      } else {
        return this.$route?.params.formId
      }
    },

    async submitPayment() {
      console.log('** Submitting payment with Stripe... **')
      try {
        this.toggleLoadingOverlayStatus(true)
        this.processingPayment = true
        this.setOverlayMessage('Processing payment...')
        
        if (!this.stripe.cardElement) {
          throw new Error('Card element not initialized')
        }
        
        // 1. Create PaymentIntent on backend
        const intentResponse = await this.createPaymentIntent(this.cost, this.payInfo.level)
        const clientSecret = intentResponse.clientSecret
        
        // 2. Confirm payment with Stripe
        const { paymentIntent, error } = await this.stripe.instance.confirmCardPayment(clientSecret, {
          payment_method: {
            card: this.stripe.cardElement,
            billing_details: {
              name: this.userInfo.full_name,
              email: this.userInfo.email
            }
          }
        })
        
        if (error) {
          throw error
        }
        
        if (paymentIntent.status === 'succeeded') {
          localStorage.setItem('stripeTokenId', paymentIntent.id)
          localStorage.setItem('paypalAmt', this.cost)
          await this.handlePayment(paymentIntent.id)
        } else if (paymentIntent.status === 'requires_action') {
          throw new Error('Payment requires additional authentication')
        }
      } catch (error) {
        console.error('Payment error:', error)
        this.setAlert({ type: 'error', message: error.message || 'Payment failed. Please try again.' })
      } finally {
        this.toggleLoadingOverlayStatus(false)
        this.setOverlayMessage()
        this.processingPayment = false
      }
    },

    async createPaymentIntent(amount, level) {
      return await createPaymentIntent(
        amount,
        level,
        this.payInfo.formId,
        this.payInfo.userId,
        this.userInfo.email
      );
    },

    async handlePayment(transactionId) {
      await this.setPayments(this.payInfo.userId);
      const parentId = await this.getParentId()
      const { userId, level } = this.payInfo

      let type = level === '1' ? 'initial payment' : 'final payment'

      const amount = localStorage.getItem('paypalAmt') || '0'
      if (!this.userInfo?.uid) {
        await this.establishUserAuth()
      }
      const couponCode = localStorage.getItem(`${this.userInfo.uid}-coupon_code`) || ''
      // this.paypal.log(
      //   userId,
      //   parentId,
      //   Date.now(),
      //   level,
      //   amount,
      //   couponCode,
      //   transactionId
      // );
      if(transactionId !== 'No Paypal Transaction') {
        this.trackEvent('event','payment_' + level, {
          couponCode: couponCode,
          type: this.$route.params.type
        })
        this.trackEvent('event', 'conversion', {
          send_to: `${this.awID}/${this.paywallIds[level - 1]}`,
          value: amount,
          currency: 'USD',
          transaction_id: transactionId
        })
      }

      // localStorage.removeItem("paypalAmt")
      await this.createUserForm(parentId, type)
      return
    },

    async createUserForm (parentId, type) {
      try {
        const user = {
          name: this.userInfo.full_name,
          email: this.userInfo.email
        }

        // Send email but don't block on it - redirect should happen regardless
        sendEmailUtil(this.baseUrl, user, type, "Thank you for your payment", '', 'sign-in', null, '', {cost: this.cost})
          .catch(error => {
            console.warn('Email sending failed but payment was successful:', error)
            // Don't throw - allow redirect to happen
          })

        if (this.payInfo.level == "2" || this.payInfo.level === 2) {
          console.log('Processing level 2 payment for formId:', this.payInfo.formId)
          await this.$nextTick()
          await this.saveStep({docId: this.payInfo.formId, step: 5})
          console.log('Saved step successfully')
          await this.toggleLoadingOverlayStatus(false)
          this.setOverlayMessage()
          console.log('Redirecting to form:', ` Redirecting to payment for level 2 from step 4 */legal-form-guide/divorce-forms/${this.payInfo.formId}`)
          // Add a small delay to ensure Firestore has persisted the change
          await new Promise(resolve => setTimeout(resolve, 500))
          await this.$router.push(`/legal-form-guide/divorce-forms/${this.payInfo.formId}`)
        } else {
          console.log('Processing level 1 payment')
          await this.loadForm({parentId: this.payInfo.formId})
          const formId = await this.saveForm(false)
          await Promise.all([
            copyWorkflowFiles({
              oldWorkflowId: parentId,
              newWorkflowId: formId,
              oldWorkflowName: 'workflows-published',
              newWorkflowName: 'workflows-user'
            }),
            this.updateFormDocs({ id: formId }),
            this.setCoupons({}),
            this.clearPublishedWorkflows()
          ])
        
          await this.toggleLoadingOverlayStatus(false)
          this.setOverlayMessage()
          await this.$router.push(`/legal-form-guide/divorce-forms/${formId}`)
        }
      } catch (error) {
        console.error('Error in createUserForm:', error)
        await this.toggleLoadingOverlayStatus(false)
        this.setOverlayMessage()
        this.setAlert({ type: 'error', message: 'Payment processed but failed to redirect. Please refresh the page.' })
        throw error
      }
    },

    async skipPayment() {
      this.setOverlayMessage('Please wait while we build your questionnaire.')
      await Promise.all([
        this.toggleLoadingOverlayStatus(true),
        this.handlePayment('No Paypal Transaction')
      ])
      await this.toggleLoadingOverlayStatus(false);
      return
    },

    async sendPayment() {
      this.loading = true;

      try {
        if (this.cost > 0) {
          // localStorage.setItem("paypalAmt", this.cost)
          // const resp = await this.paypal.requestPayment(this.cost, this.payInfo.level, location.pathname)
          this.submitted = true;
        } else {
          this.setOverlayMessage('Please wait while we build your questionnaire.')
          await Promise.all([
            this.toggleLoadingOverlayStatus(true),
            this.handlePayment('No Paypal Transaction')
          ])
          await this.toggleLoadingOverlayStatus(false)
          this.setOverlayMessage()
        }
      } catch (error) {
        this.$logger.error(error);
      }
      this.loading = false;
      return
    },
  }
}